import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";
import {
  GET_PHARMACY_TEST_LIST,
  GET_LAB_TEST_LIST,
  ADD_LAB_TEST,
  ADD_LAB_TEST_PACKAGE,
} from "../../services/urls";
import { getPharmacyId } from "../../utils/commonFunctions";

// Initial state
const initialState = {
  pharmacyLoading: false,
  loading: false,
  loadingUpdate: false,
  loadingDelete: false,
  labTests: [],
  pharmacyTests: [],
  customPrice: null,
  error: null,
};

// Async thunk to fetch pharmacy lab tests (existing)
export const fetchPharmacyLabTests = createAsyncThunk(
  "labTests/fetchPharmacyLabTests",
  async ({ limit = 10, page = 1, search = "" }, { rejectWithValue }) => {
    try {
      const response = await api.get(GET_PHARMACY_TEST_LIST, {
        params: { limit, page, search },
      });
      return response.data.data; // Return the pharmacy lab test data
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch pharmacy lab tests"
      );
    }
  }
);

// Async thunk to fetch lab tests for the modal
export const fetchLabTestsForModal = createAsyncThunk(
  "labTests/fetchLabTestsForModal",
  async ({ search = "", testType = "" }, { rejectWithValue }) => {
    try {
      const response = await api.get(
        GET_LAB_TEST_LIST + `/${getPharmacyId()}`,
        {
          params: { page: 1, limit: 100, search, hideExisting: true, testType },
        }
      );
      return response.data.data.results; // Return the lab test data for modal
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch lab tests for modal"
      );
    }
  }
);

// Async thunk to add a lab test (new)
export const addLabTest = createAsyncThunk(
  "labTests/addLabTest",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post(ADD_LAB_TEST, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      // Some endpoints return 200 with { status: false, message, data: null }
      if (response?.data?.status === false) {
        return rejectWithValue(response?.data?.message || "Failed to add lab test");
      }
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add lab test"
      );
    }
  }
);

// Async thunk to add a lab test (new)
export const updateLabTest = createAsyncThunk(
  "labTests/updateLabTest",
  async ({ _id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(ADD_LAB_TEST + `/${_id}`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data; // Assuming the API returns the added lab test
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add lab test"
      );
    }
  }
);

// Async thunk to delete a lab test (new)
export const deleteLabTest = createAsyncThunk(
  "labTests/deleteLabTest",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.delete(
        ADD_LAB_TEST + `${getPharmacyId()}/${id}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete lab test"
      );
    }
  }
);

// Async thunk to add a lab test package
export const addLabTestPackage = createAsyncThunk(
  "labTests/addLabTestPackage",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post(ADD_LAB_TEST_PACKAGE, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      if (response?.data?.status === false) {
        return rejectWithValue(
          response?.data?.message || "Failed to add lab test package"
        );
      }
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add lab test package"
      );
    }
  }
);

const labTestsSlice = createSlice({
  name: "labTests",
  initialState,
  reducers: {
    setCustomPriceAction: (state, action) => {
      state.customPrice = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPharmacyLabTests.pending, (state) => {
        state.pharmacyLoading = true;
        state.error = null;
      })
      .addCase(fetchPharmacyLabTests.fulfilled, (state, action) => {
        state.pharmacyLoading = false;
        state.pharmacyTests = action.payload; // Store pharmacy lab tests
      })
      .addCase(fetchPharmacyLabTests.rejected, (state, action) => {
        state.pharmacyLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchLabTestsForModal.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLabTestsForModal.fulfilled, (state, action) => {
        state.loading = false;
        state.labTests = action.payload; // Store lab tests for modal
      })
      .addCase(fetchLabTestsForModal.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addLabTest.pending, (state) => {
        state.loadingUpdate = true;
        state.error = null;
      })
      .addCase(addLabTest.fulfilled, (state, action) => {
        state.loadingUpdate = false;
        state.labTests = [...state.labTests, action.payload];
      })
      .addCase(addLabTest.rejected, (state, action) => {
        state.loadingUpdate = false;
        state.error = action.payload;
      })
      .addCase(updateLabTest.pending, (state) => {
        state.loadingUpdate = true;
      })
      .addCase(updateLabTest.fulfilled, (state, action) => {
        state.loadingUpdate = false;
        state.error = null;
      })
      .addCase(updateLabTest.rejected, (state, action) => {
        state.loadingUpdate = false;
        state.error = action.payload;
      })
      .addCase(deleteLabTest.pending, (state) => {
        state.loadingDelete = true;
      })
      .addCase(deleteLabTest.fulfilled, (state, action) => {
        state.loadingDelete = false;
        state.error = null;
      })
      .addCase(deleteLabTest.rejected, (state, action) => {
        state.loadingDelete = false;
        state.error = action.payload;
      })
      .addCase(addLabTestPackage.pending, (state) => {
        state.loadingUpdate = true;
        state.error = null;
      })
      .addCase(addLabTestPackage.fulfilled, (state, action) => {
        state.loadingUpdate = false;
        state.labTests = [...state.labTests, action.payload];
      })
      .addCase(addLabTestPackage.rejected, (state, action) => {
        state.loadingUpdate = false;
        state.error = action.payload;
      });
  },
});

export const { setCustomPriceAction } = labTestsSlice.actions;
export default labTestsSlice.reducer;
